import { useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { z } from "zod";
import { Flex, Spinner, Text } from "@radix-ui/themes";
import { isAxiosError } from "axios";
import { CustomReport } from "@/v2/features/custom-report/CustomReport";
import { CustomReportEmptyState } from "@/v2/features/custom-report/CustomReportEmptyState";
import { useCustomReportApi } from "@/v2/entities/custom-report/CustomReport.api";

function useExploreViewId() {
  const { exploreViewId } = useParams();
  return z.string().min(1).parse(exploreViewId);
}

function CustomReportPage() {
  const exploreViewId = useExploreViewId();
  const { getReport, getNextReportDue } = useCustomReportApi();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["custom-report", exploreViewId],
    queryFn: () => getReport(exploreViewId),
    enabled: !!exploreViewId,
    retry: (failureCount, error) => {
      if (isAxiosError(error) && error.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });

  const is204Response = data === null && !isError;
  const is404Error =
    isError && isAxiosError(error) && error.response?.status === 404;

  const { data: nextReportDue, isLoading: isLoadingNextReport } = useQuery({
    queryKey: ["custom-report-next-due", exploreViewId],
    queryFn: () => getNextReportDue(exploreViewId),
    enabled: !!exploreViewId && is204Response,
  });

  if (isLoading) {
    return (
      <Flex align="center" justify="center" height="100vh">
        <Spinner size="3" />
        <Text size="3" color="gray" style={{ marginLeft: 12 }}>
          Loading...
        </Text>
      </Flex>
    );
  }

  if (isError) {
    if (is404Error) {
      return <CustomReportEmptyState is404={true} />;
    }

    const errorMessage =
      (error as any)?.message ||
      "Failed to load report. Please try again later.";

    return (
      <Flex
        align="center"
        justify="center"
        height="100vh"
        direction="column"
        gap="3"
      >
        <Text size="4" weight="medium" color="red">
          Error loading report
        </Text>
        <Text size="3" color="gray">
          {errorMessage}
        </Text>
      </Flex>
    );
  }

  if (is204Response) {
    if (isLoadingNextReport) {
      return (
        <Flex align="center" justify="center" height="100vh">
          <Spinner size="3" />
          <Text size="3" color="gray" style={{ marginLeft: 12 }}>
            Loading...
          </Text>
        </Flex>
      );
    }
    return (
      <CustomReportEmptyState nextReportDue={nextReportDue} is404={false} />
    );
  }

  if (!data) {
    return <CustomReportEmptyState />;
  }

  const { report, startDate, endDate } = data;

  return (
    <CustomReport
      tenantId={report.tenantId}
      title={report.title}
      startDate={startDate}
      endDate={endDate}
      sections={report.sections}
    />
  );
}

export default CustomReportPage;
